import { UzCheckbox, UzInput } from '@utazzo/ui';
import { Plus, X } from 'lucide-react';
import React, { useState } from 'react';

/**
 * Amenity selector: checkbox grid of the common hotel amenities plus an
 * add-your-own input for anything unusual. Value is a plain string array -
 * exactly what the API's amenities jsonb column stores.
 */

const COMMON_AMENITIES: ReadonlyArray<string> = [
  'Swimming pool',
  'Spa',
  'Gym',
  'Free WiFi',
  'Restaurant',
  'Bar',
  'Room service',
  'Air conditioning',
  'Breakfast',
  'Parking',
  'Airport transfer',
  'Beach access',
  'Kids club',
  'Laundry',
  'Pet friendly',
  'Wheelchair accessible',
  'EV charging',
  'Banquet hall',
];

interface AmenityGridProps {
  value: string[];
  onChange: (next: string[]) => void;
  disabled?: boolean;
}

export const AmenityGrid = ({ value, onChange, disabled }: AmenityGridProps): React.JSX.Element => {
  const [customDraft, setCustomDraft] = useState('');

  const selected = new Set(value.map((v) => v.toLowerCase()));
  const isOn = (a: string): boolean => selected.has(a.toLowerCase());

  const toggle = (amenity: string): void => {
    if (isOn(amenity)) {
      onChange(value.filter((v) => v.toLowerCase() !== amenity.toLowerCase()));
    } else {
      onChange([...value, amenity]);
    }
  };

  const addCustom = (): void => {
    const t = customDraft.trim();
    if (!t || isOn(t)) {
      setCustomDraft('');
      return;
    }
    onChange([...value, t]);
    setCustomDraft('');
  };

  // Anything selected that is not in the common list (legacy data / custom).
  const customSelected = value.filter(
    (v) => !COMMON_AMENITIES.some((c) => c.toLowerCase() === v.toLowerCase()),
  );

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 sm:grid-cols-3">
        {COMMON_AMENITIES.map((amenity) => (
          <UzCheckbox
            key={amenity}
            label={amenity}
            checked={isOn(amenity)}
            onCheckedChange={() => toggle(amenity)}
            disabled={disabled}
          />
        ))}
      </div>

      {customSelected.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {customSelected.map((amenity) => (
            <span
              key={amenity}
              className="inline-flex w-fit items-center gap-1 rounded-full border border-zinc-200 bg-zinc-50 py-0.5 pr-1 pl-2.5 text-xs text-zinc-700"
            >
              {amenity}
              <button
                type="button"
                onClick={() => toggle(amenity)}
                disabled={disabled}
                aria-label={`Remove ${amenity}`}
                className="rounded-full p-0.5 text-zinc-400 hover:bg-zinc-200 hover:text-zinc-700"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}

      <div className="flex items-end gap-2">
        <UzInput
          label="Add another amenity"
          placeholder="e.g. Private butler"
          value={customDraft}
          onChange={(e) => setCustomDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              addCustom();
            }
          }}
          disabled={disabled}
          containerClassName="max-w-xs"
        />
        <button
          type="button"
          onClick={addCustom}
          disabled={disabled || !customDraft.trim()}
          className="inline-flex h-10 items-center gap-1 rounded-md border border-zinc-200 px-3 text-sm text-zinc-700 transition-colors hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <Plus className="h-3.5 w-3.5" />
          Add
        </button>
      </div>
    </div>
  );
};
